<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.registeredUsers`) }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex xs1 />
            <v-flex xs1>{{ $t(`${namespace}.userId`) }}</v-flex>
            <v-flex xs4>{{ $t('Common.user') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs4>{{ $t('Common.email') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs3>{{ $t('Common.accessProfile') | capitalizeFirstLetter }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex xs1>
                        <v-tooltip
                          v-if="hasRole('user', 'ROLE_PUT_INTERNAL_USERS')"
                          slot="append"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleEditUser(item)"
                          >
                            <v-icon>edit</v-icon>
                          </v-btn>
                          <span>{{ $t(`Common.edit`) }}</span>
                        </v-tooltip>

                        <v-tooltip
                          v-if="hasRole('user', 'ROLE_DELETE_INTERNAL_USERS')"
                          slot="append"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="handleRemoveUser(item)"
                          >
                            <v-icon>delete</v-icon>
                          </v-btn>
                          <span>{{ $t(`Common.delete`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex xs1>
                        <span :title="item.corporateId" class="text--ellipsis" contenteditable>
                          {{ item.corporateId || '-' }}
                        </span>
                      </v-flex>

                      <v-flex xs4>
                        <span :title="item.name" class="text--ellipsis">
                          {{ item.name || '-' }}
                        </span>
                      </v-flex>

                      <v-flex xs4>
                        <span :title="item.email" class="text--ellipsis">
                          {{ item.email || '-' }}
                        </span>
                      </v-flex>

                      <v-flex xs3>
                        <span :title="item.profile" class="text--ellipsis">
                          {{ getProfileName(item.profile) }}
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="employee"
          :text="$t(`${namespace}.noneUsersFound`)"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <UserDrawer
      :show="drawer.visibility"
      :user="selectedItem"
      @closeDrawer="closeDrawer"
      @deleteUser="openSingleDeleteDialog"
    />

    <SingleDeleteDialog
      :visible="singleDeleteDialog"
      :item="selectedItem"
      @close="closeSingleDeleteDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { isArray, formatNumber, objectToArray, hasRole } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import UserDrawer from 'components/users/drawer'
import SingleDeleteDialog from './dialogs/SingleDeleteDialog'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    EmptyState,
    UserDrawer,
    SingleDeleteDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.UsersPage',
      selectedItem: null,
      checkedAllItems: false,
      drawer: {
        visibility: false,
        type: null
      },
      selectedCompanies: [],
      singleDeleteDialog: false
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidArray (val) {
      return isArray(val)
    },

    openDrawer (type = 'preview') {
      this.drawer = {
        visibility: true,
        type
      }
    },

    closeDrawer (reload = null) {
      this.drawer = {
        visibility: false,
        type: null
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    handlePage (page) {
      this.closeDrawer()
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      if (this.selectedItem && this.selectedItem.id) {
        return item.id === this.selectedItem.id
      }
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item
        this.openDrawer()
      }
    },

    handleEditUser (user) {
      this.$router.push({
        name: 'User',
        params: {
          type: 'editar',
          id: user.corporateId
        }
      })
    },

    handleRemoveUser (user) {
      this.openSingleDeleteDialog(user)
    },

    openSingleDeleteDialog (item) {
      this.singleDeleteDialog = true
      this.selectedItem = item
    },

    closeSingleDeleteDialog (reload) {
      this.singleDeleteDialog = false
      if (!this.drawer.visibility) this.selectedItem = null
      if (reload) {
        this.closeDrawer()
        this.handleReloadData()
      }
    },

    getProfileName (value) {
      const profiles = objectToArray(this.$t('Views.UserPage.form.profiles'))
      const targetProfile = profiles.find(profile => profile.value === value)
      return targetProfile ? targetProfile.name : '-'
    }
  },
  watch: {
    selectedCompany () {
      this.closeDrawer()
    },

    drawerVisibility () {
      this.closeDrawer()
    }
  }
}
</script>
